import {
  GetUserAnalyticsInfoQuery,
  useGetUserAnalyticsInfoQuery,
} from "modules/graphql.types";
import Script from "next/script";
import Intercom from "../../modules/intercom";
import { printDevError } from "@/flokit/utils/debug/print-dev-error";
const isProduction = process.env.NODE_ENV === "production";

function initializePendo(data: GetUserAnalyticsInfoQuery) {
  const payload = data?.userAnalyticsInfo;
  switch (payload?.__typename) {
    case "AdminUserAnalyticsInfo": {
      // We currently only run Pendo in production, and we only use the user's ID. This is to maintain visitor/account ID consistency, while still accomplishing uniqueness of ID between environment and product
      // Eventually we can migrate the old Pendo IDs to match our current Pendo visitor ID convention https://support.pendo.io/hc/en-us/articles/360051378732-Data-Deletion-and-Manipulation-Services
      const visitorId = isProduction ? payload.id : payload.pendoVisitorId;
      const accountId = isProduction ? payload.orgId : payload.pendoAccountId;

      // @ts-ignore
      window.pendo.initialize({
        visitor: {
          id: visitorId,
          email: payload.email,
          // eslint-disable-next-line camelcase
          full_name: payload.fullName,
          role: payload.isAdmin,
        },

        account: {
          id: accountId,
          name: payload.orgName,
          // eslint-disable-next-line camelcase
          is_paying: payload.isPaying,
        },
      });
      break;
    }
    case "CandidateUserAnalyticsInfo": {
      // @ts-ignore
      window.pendo.initialize({
        visitor: {
          id: payload.pendoVisitorId,
          loggedInEmail: payload.loggedInEmail,
          primaryEmail: payload.forwardAccount?.primaryEmail ?? null,
          // eslint-disable-next-line camelcase
          number_of_emails: payload.forwardAccount?.numberOfEmails ?? null,
          // eslint-disable-next-line camelcase
          full_name: payload.fullName,
          // eslint-disable-next-line camelcase
          has_forward_account: !!payload.forwardAccount,
          // eslint-disable-next-line camelcase
          has_active_forward_account: payload.forwardAccount
            ? payload.forwardAccount.isActive
            : false,
          // eslint-disable-next-line camelcase
          active_forward_account_since: payload.forwardAccount
            ? payload.forwardAccount.activeSince
            : null,
          // eslint-disable-next-line camelcase
          graduation_date: payload.biographicalInfo
            ? payload.biographicalInfo.graduationDate
            : null,
          // eslint-disable-next-line camelcase
          graduation_year: payload.biographicalInfo
            ? payload.biographicalInfo.graduationYear
            : null,
          // eslint-disable-next-line camelcase
          law_school: payload.biographicalInfo
            ? payload.biographicalInfo.lawSchool
            : null,
          // eslint-disable-next-line camelcase
          class_year: payload.biographicalInfo
            ? payload.biographicalInfo.humanReadableLawSchoolClassification
            : null,
          // eslint-disable-next-line camelcase
          undergraduate_major: payload.biographicalInfo
            ? payload.biographicalInfo.undergraduateMajor
            : null,
          gpa: payload.biographicalInfo ? payload.biographicalInfo.gpa : null,
          // eslint-disable-next-line camelcase
          office_location_preferences:
            payload?.forwardAccount?.discoveryQuizAnswers?.officeLocations?.map(
              (officeLocation) => officeLocation.name,
            ) ?? null,
          // eslint-disable-next-line camelcase
          practice_category_preferences:
            payload?.forwardAccount?.discoveryQuizAnswers?.practiceCategories?.map(
              (practiceCategory) => practiceCategory.name,
            ) ?? null,
          ethnicities:
            payload?.forwardAccount?.discoveryQuizAnswers?.demographicInfo
              ?.ethnicityAnswers ?? null,
          genders:
            payload?.forwardAccount?.discoveryQuizAnswers?.demographicInfo
              ?.genderAnswers ?? null,
          // eslint-disable-next-line camelcase
          veteran_status:
            payload?.forwardAccount?.discoveryQuizAnswers?.demographicInfo
              ?.veteranStatusAnswer ?? null,
          // eslint-disable-next-line camelcase
          sexual_identity:
            payload?.forwardAccount?.discoveryQuizAnswers?.demographicInfo
              ?.sexualIdentityAnswer ?? null,
        },

        account: {
          id: payload.pendoAccountId,
        },
      });
      break;
    }
    default:
      // @ts-ignore
      window.pendo.initialize({
        visitor: {
          id: null,
          email: null,
          // eslint-disable-next-line camelcase
          full_name: null,
          role: false,
        },

        account: {
          id: null,
          name: null,
          // eslint-disable-next-line camelcase
          is_paying: null,
        },
      });
  }
}

function initializeFullStory(data: GetUserAnalyticsInfoQuery) {
  const payload = data?.userAnalyticsInfo;
  switch (payload?.__typename) {
    case "AdminUserAnalyticsInfo": {
      const fullStoryId = isProduction ? payload.id : payload.fullStoryId;
      // @ts-ignore
      FS.identify(fullStoryId, {
        displayName: payload.fullName,
        email: payload.email,
      });

      // @ts-ignore; Pendo and FS objects not defined
      window.pendo.identify({
        visitor: {
          id: fullStoryId,
          // @ts-ignore
          mostRecentFullStorySession: FS.getCurrentSessionURL(),
        },
      });

      break;
    }
    case "CandidateUserAnalyticsInfo": {
      // @ts-ignore
      FS.identify(payload.fullStoryId, {
        displayName: payload.fullName,
        email: payload.loggedInEmail,
      });

      // @ts-ignore
      window.pendo.identify({
        visitor: {
          id: payload.fullStoryId,
          // @ts-ignore
          mostRecentFullStorySession: FS.getCurrentSessionURL(),
        },
      });

      break;
    }
    // Do nothing if no analytics, e.g. if not logged in
    default:
      return;
  }
}

export const AnalyticsProvider = () => {
  const userAnalyticsInfoFetcher = useGetUserAnalyticsInfoQuery.fetcher();
  return (
    <>
      <Intercom />
      {/* Must come before FullStory */}
      <Script
        strategy="afterInteractive"
        src={"/v2/assets/scripts/pendo.js"}
        onLoad={() => {
          userAnalyticsInfoFetcher()
            .then(initializePendo)
            .catch((error) => {
              printDevError(error);
            });
        }}
      />
      {/* Must come after Pendo so that most recent Full Story session URL can be sent to Pendo */}
      <Script
        strategy="afterInteractive"
        src={"/v2/assets/scripts/fullstory.js"}
        onLoad={() => {
          userAnalyticsInfoFetcher()
            .then(initializeFullStory)
            .catch((error) => {
              printDevError(error);
            });
        }}
      />
    </>
  );
};
